<template>
    <div v-click-outside="hide">
        <span ref="input" @click="showDatePicker">
            <Input 
                type="text"
                :placeholder="placeholder"
                :disabled="disabled"
                :modelValue="showedDate"
            />
        </span>
        <Teleport to="body">
            <div :class="{'picker': true, 'picker--opened': show}" :style="{
                top: top,
                left: left,
                width: width,
                'z-index': zIndex
            }" ref="picker">
                <div class="picker__holder"  tabindex="-1">
                    <div class="picker__frame">
                        <div class="picker__wrap">
                            <div class="picker__box" @click.stop>
                                <div class="picker__header">
                                    <div class="picker__month">{{ months[currentDisplayDate.getMonth()] }}</div>
                                <div class="picker__year">{{ currentDisplayDate.getFullYear() }}</div>
                                <div class="picker__nav--prev" data-nav="-1" role="button" aria-controls="P916051175_table" @click.stop="goToPreviousMonth"
                                    title="Previous month"> </div>
                                <div class="picker__nav--next" data-nav="1" role="button" aria-controls="P916051175_table" @click.stop="goToNextMonth"
                                    title="Next month"> </div>
                            </div>
                            <table class="picker__table" id="P916051175_table" role="grid" aria-controls="P916051175"
                                aria-readonly="true">
                                <thead>
                                    <tr>
                                        <th class="picker__weekday" scope="col" v-for="day in daysOfWeek">{{ day }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="week in calendarGrid">
                                        <td role="presentation" v-for="day in week">
                                            <div :class="[day.isCurrentMonth ? 'picker__day picker__day--infocus' : 'picker__day picker__day--outfocus', day.isToday ? 'picker__day--today' : '', day.selected ? 'picker__day--selected picker__day--highlighted' : '']"
                                                @click.stop="selectDate(day.day, day.month, day.year, true)" role="gridcell">{{ day.day }}</div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <div class="daterangepicker_input">
                                <div v-if="withTime" class="picker__time">
                                    <select v-model.number="selectedHour" class="hourselect form-control input-sm">
                                        <option v-for="h in 24" :key="h - 1" :value="h - 1">
                                            {{ String(h - 1).padStart(2, '0') }}
                                        </option>
                                    </select>
                                    <span class="picker__time-separator">:</span>
                                    <select v-model.number="selectedMinute" class="hourselect form-control input-sm">
                                        <option v-for="m in 60" :key="m - 1" :value="m - 1">
                                            {{ String(m - 1).padStart(2, '0') }}
                                        </option>
                                    </select>
                                    <span class="picker__time-separator">:</span>
                                    <select v-model.number="selectedSecond" class="hourselect form-control input-sm">
                                        <option v-for="s in 60" :key="s - 1" :value="s - 1">
                                            {{ String(s - 1).padStart(2, '0') }}
                                        </option>
                                    </select>
                                </div>
                            </div>

                            <div class="picker__footer">
                                <button class="picker__button--today" @click.stop="goToday">{{ translate('buttons.today') }}</button>
                                <button class="picker__button--clear" @click.stop="clear">{{ translate('buttons.clear') }}</button>
                                <button class="picker__button--close" @click.stop="showDatePicker">{{ translate('buttons.close') }}</button></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Teleport>
    </div>
</template>
<script>
import vClickOutside from 'click-outside-vue3'
import Input from "../Input.vue";

// modelValue приходить як Date або як рядок, тому всередині завжди працюємо з Date або null
function toDate(value) {
    if (value === null || value === undefined || value === '') {
        return null
    }

    const date = new Date(value)

    if (isNaN(date.getTime())) {
        return null
    }

    return date
}

export default {
    name: 'DatePicker',
    components: {
        Input
    },
    props: {
        modelValue: {
            type: [Date, String],
            default: null
        },
        disabled: {
            type: Boolean,
            default: false
        },
        placeholder: {
            type: String,
            default: () => translate('components.input.placeholder')
        },
        withTime: {
            type: Boolean,
            default: false
        }
    },
    emits: ['update:modelValue'],
    data() {
        return {
            currentDate: new Date(),
            currentDisplayDate: new Date(),
            calendarGrid: [],
            daysOfWeek: [ 
                translate('labels.monday'),
                translate('labels.tuesday'),
                translate('labels.wednesday'),
                translate('labels.thursday'),
                translate('labels.friday'),
                translate('labels.saturday'),
                translate('labels.sunday')
            ],
            months: [
                translate('labels.january'),
                translate('labels.february'),
                translate('labels.march'),
                translate('labels.april'),
                translate('labels.may'),
                translate('labels.june'),
                translate('labels.july'),
                translate('labels.august'),
                translate('labels.september'),
                translate('labels.october'),
                translate('labels.november'),
                translate('labels.december')
            ],
            selectedDay: toDate(this.modelValue),
            show: false,
            top: 0,
            left: 0,
            width: 0,
            zIndex: 950,
            selectedHour: 0,
            selectedMinute: 0,
            selectedSecond: 0
        }
    },
    methods: {
        calculatePosition() {
            const input = this.$refs.input.getBoundingClientRect(),
                  picker = this.$refs.picker.getBoundingClientRect();

            if (this.position === 'top') {
                this.top = input.top + window.pageYOffset - picker.height - 6 + 'px'
            } else {
                this.top = input.top + window.pageYOffset + input.height + 2 + 'px'
            }

            this.left = input.left + 'px'
            this.width = input.width + 'px'
        },
        getDaysInMonth(year, month) {
            const firstDayOfNextMonth = new Date(year, month + 1, 1);
            const lastDayOfMonth = new Date(firstDayOfNextMonth - 1);
            const lastDay = lastDayOfMonth.getDate();

            return lastDay;
        },
        generateCalendarGrid() {
            const currentYear = this.currentDisplayDate.getFullYear();
            const currentMonth = this.currentDisplayDate.getMonth();

            const daysInMonth = this.getDaysInMonth(currentYear, currentMonth);
            const firstDayOfWeek = new Date(currentYear, currentMonth, 1).getDay();
            const lastDayOfWeek = new Date(currentYear, currentMonth, daysInMonth).getDay();

            // Визначимо кількість пустих комірок для додавання днів з попереднього місяця в початок календаря
            const emptyCellsAtStart = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
            // Визначимо кількість пустих комірок для додавання днів з наступного місяця в кінець календаря
            const emptyCellsAtEnd = lastDayOfWeek === 0 ? 0 : 7 - lastDayOfWeek;

            // Змінні для збереження днів попереднього та наступного місяців
            const daysInPrevMonth = this.getDaysInMonth(currentYear, currentMonth - 1);
            const daysInNextMonth = this.getDaysInMonth(currentYear, currentMonth + 1);

            // Додамо дні попереднього місяця до календаря
            const prevMonthDays = [];
            for (let i = emptyCellsAtStart - 1; i >= 0; i--) {
                prevMonthDays.push({
                    day: daysInPrevMonth - i,
                    month: currentMonth !== 1 ? currentMonth - 1 : 11,
                    year: currentMonth === 1 ? currentYear - 1 : currentYear,
                    selected: false,
                    isCurrentMonth: false,
                    isToday: false
                });
            }

            // Додамо дні поточного місяця до календаря
            const currentMonthDays = [];
            for (let day = 1; day <= daysInMonth; day++) {
                currentMonthDays.push({
                    day,
                    month: currentMonth,
                    year: currentYear,
                    selected: false,
                    isCurrentMonth: true,
                    isToday: day === this.currentDate.getDate() && currentMonth === this.currentDate.getMonth() && currentYear === this.currentDate.getFullYear()
                });
            }

            // Додамо дні наступного місяця до календаря
            const nextMonthDays = [];
            for (let i = 0; i < emptyCellsAtEnd; i++) {
                nextMonthDays.push({
                    day: i + 1,
                    month: currentMonth !== 11 ? currentMonth + 1 : 0,
                    year: currentMonth === 11 ? currentYear + 1 : currentYear,
                    selected: false,
                    isCurrentMonth: false,
                    isToday: false
                });
            }

            // Об'єднаємо дні попереднього, поточного та наступного місяців в один масив
            const calendarGrid = [...prevMonthDays, ...currentMonthDays, ...nextMonthDays];

            // Розділімо calendarGrid на рядки, щоб отримати двовимірний масив
            const calendarRows = [];
            for (let i = 0; i < calendarGrid.length; i += 7) {
                calendarRows.push(calendarGrid.slice(i, i + 7));
            }
            this.calculatePosition()
            this.calendarGrid = calendarRows;
        },
        // Метод для переходу на попередній місяць
        goToPreviousMonth() {
            this.currentDisplayDate = new Date(this.currentDisplayDate.getFullYear(), this.currentDisplayDate.getMonth() - 1, 1);
            this.generateCalendarGrid();
            this.restoreSelection()
        },
        // Метод для переходу на наступний місяць
        goToNextMonth() {
            this.currentDisplayDate = new Date(this.currentDisplayDate.getFullYear(), this.currentDisplayDate.getMonth() + 1, 1);
            this.generateCalendarGrid();
            this.restoreSelection()
        },
        // Метод для підсвічування вибраного дня після перемальовування сітки
        restoreSelection() {
            const date = toDate(this.modelValue)

            if (date === null) {
                return
            }

            this.selectDate(date.getDate(), date.getMonth(), date.getFullYear(), false)
        },
        selectDate(selectedDay, selectedMonth, selectedYear, changeMonth)  {
            let date = toDate(this.modelValue)

            if(changeMonth) {
                let time = new Date()
                const hour = this.withTime ? this.selectedHour : time.getHours();
                const minute = this.withTime ? this.selectedMinute : time.getMinutes();
                const second = this.withTime ? this.selectedSecond : time.getSeconds();
                date = new Date(selectedYear, selectedMonth, selectedDay, hour, minute, second)
            }

            if (date === null) {
                return
            }

            if (this.selectedDay !== null && changeMonth && this.selectedDay.getDate() === selectedDay && this.selectedDay.getMonth() === selectedMonth && this.selectedDay.getFullYear() === selectedYear) {
                this.selectedDay = null
                this.calendarGrid.map(week => {
                    week.map(day => day.selected = false)
                })
            } else {
                this.selectedDay = new Date(date);
                if (this.withTime) {
                    const dateString = `${String(this.selectedDay.getFullYear())}-${String(this.selectedDay.getMonth() + 1).padStart(2, '0')}-${String(this.selectedDay.getDate()).padStart(2, '0')} ${String(this.selectedDay.getHours()).padStart(2, '0')}:${String(this.selectedDay.getMinutes()).padStart(2, '0')}:${String(this.selectedDay.getSeconds()).padStart(2, '0')}`;
                    this.$emit('update:modelValue', dateString)
                } else {
                    this.$emit('update:modelValue', `${String(this.selectedDay.getFullYear())}-${String(this.selectedDay.getMonth() + 1).padStart(2, '0')}-${String(this.selectedDay.getDate()).padStart(2, '0')}`)
                }
                this.calendarGrid.map(week => {
                    week.map(day => day.selected = day.day === selectedDay && day.month === selectedMonth && day.year === selectedYear ? true : false)
                })
            }
        },
        showDatePicker() {
            this.currentDisplayDate = new Date();
            this.generateCalendarGrid();
            const date = toDate(this.modelValue)
            if(date !== null) {
                if (this.withTime) {
                    this.selectedHour = date.getHours();
                    this.selectedMinute = date.getMinutes();
                    this.selectedSecond = date.getSeconds();
                }
                this.selectDate(date.getDate(), date.getMonth(), date.getFullYear(), false);
            }
            this.show = !this.show
        },
        goToday() {
            this.currentDisplayDate = new Date();
            this.generateCalendarGrid();
            this.selectDate(this.currentDate.getDate(), this.currentDate.getMonth(), this.currentDate.getFullYear(), true)
        },
        clear() {
            this.selectedDay = null;
            this.calendarGrid.map(week => {
                    week.map(day => day.selected = false)
                })
            this.selectedHour = 0;
            this.selectedMinute = 0;
            this.selectedSecond = 0;
            this.$emit('update:modelValue', null)
        },
        hide() {
            this.show = false
        },
        updateTimeInModelValue() {
            const date = toDate(this.modelValue)

            if (date !== null && this.withTime) {
                date.setHours(this.selectedHour);
                date.setMinutes(this.selectedMinute);
                date.setSeconds(this.selectedSecond);
                
                const dateString = `${String(date.getFullYear())}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`;
                this.$emit('update:modelValue', dateString);
            }
        }
    },
    watch: {
        // Проп може змінитися ззовні (скидання фільтра, перевикористання інстансу
        // у v-for), тому selectedDay не можна лишати з попереднім значенням
        modelValue(value) {
            this.selectedDay = toDate(value);
        },
        selectedHour() {
            this.updateTimeInModelValue();
        },
        selectedMinute() {
            this.updateTimeInModelValue();
        },
        selectedSecond() {
            this.updateTimeInModelValue();
        }
    },
    computed: {
        showedDate() {
            const date = toDate(this.modelValue)
            if(date !== null) {
                const dateString = `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear().toString()}`;
                
                if (this.withTime) {
                    const timeString = `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}`;
                    return `${dateString} ${timeString}`;
                }
                
                return dateString;
            } else {
                return ''
            }
        }
    },
    mounted() {
        this.calculatePosition()

        let modalCount = document.getElementsByClassName('modal-backdrop').length

        if (modalCount > 0) {
            this.zIndex = 1000 + (modalCount * 10) + 7
        }
    },
    directives: {
        clickOutside: vClickOutside.directive
    }
}
</script>
<style scoped>
.picker__holder {
    max-width: 100%;
}

.picker__time {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 5px;
    padding: 10px;
}
</style>
